import api from "../../../utils/api";

// ============================================================================
// Catálogo unificado de productos (CRM → Negocios → Productos).
// GET /crm/products/catalog — tres fuentes en una sola lista:
//   • crm      productos y servicios dados de alta en el CRM
//   • hubspot  productos importados de HubSpot
//   • license  licencias de la plataforma
//   • pos      productos y servicios del Punto de venta de la plataforma
// CC-CONSOLE recibe además lo de sus otras plataformas (Wayak, Nuvex), leído en
// vivo; Wayak y Nuvex reciben solo lo propio. El alcance lo decide el servidor.
// ============================================================================

export type ProductOrigin = "crm" | "hubspot" | "license" | "pos";
/** Para quién está disponible: el público, o solo el admin reseller (uso interno). */
export type ProductAudience = "public" | "reseller";

export interface CatalogProduct {
  /** Único en toda la lista (plataforma + fuente + id). */
  key: string;
  id: string;
  name: string | null;
  description: string | null;
  sku: string | null;
  price: number | null;
  cost: number | null;
  currency: string;
  type: string | null;
  origin: ProductOrigin;
  /** null: la plataforma de origen aún no informa si lo ofrece al público. */
  audience?: ProductAudience | null;
  /** Producto del CRM al que pertenece: "Coding Cloud", "Wayak" o "Nuvex". */
  platform: string;
  tenant: string;
  /** Todo lo que no es un producto propio del CRM se administra en otra pantalla. */
  readOnly: boolean;
  // Solo POS.
  category?: string | null;
  image?: string | null;
  // Licencias y servicios por suscripción del POS.
  billingCycle?: string | null;
  billingModel?: string | null;
  status?: string | null;
}

export interface CatalogSourceState {
  ok: boolean;
  error: string | null;
  count: number;
}

export interface CatalogPlatform {
  id: string;
  product: string;
  self: boolean;
  products: CatalogSourceState;
  licenses: CatalogSourceState;
}

export interface ProductCatalog {
  rows: CatalogProduct[];
  platforms: CatalogPlatform[];
  /** El servidor solo manda el precio de compra a propietario, administradores y ventas. */
  seesCost: boolean;
}

export async function fetchProductCatalog(): Promise<ProductCatalog> {
  const res = await api.get("/crm/products/catalog");
  const data = res.data?.data?.data ?? {};
  return {
    rows: Array.isArray(data.rows) ? data.rows : [],
    platforms: Array.isArray(data.platforms) ? data.platforms : [],
    seesCost: data.seesCost === true,
  };
}

export const ORIGIN_META: Record<ProductOrigin, { label: string; icon: string; tone: string }> = {
  crm: { label: "CRM", icon: "ri-price-tag-3-line", tone: "bg-primary-500/15 text-primary-500" },
  hubspot: { label: "HubSpot", icon: "ri-download-cloud-2-line", tone: "bg-orange-500/15 text-orange-600" },
  license: { label: "Licencia", icon: "ri-key-2-line", tone: "bg-emerald-500/15 text-emerald-600" },
  pos: { label: "Punto de venta", icon: "ri-store-2-line", tone: "bg-sky-500/15 text-sky-600" },
};

export const AUDIENCE_META: Record<ProductAudience, { label: string; icon: string; tone: string }> = {
  public: { label: "Público", icon: "ri-global-line", tone: "bg-emerald-500/15 text-emerald-600" },
  reseller: { label: "Admin reseller", icon: "ri-shield-user-line", tone: "bg-violet-500/15 text-violet-600" },
};

/** Etiqueta de un origen; uno que esta versión no conoce no rompe la tabla. */
export function originMeta(origin: string): { label: string; icon: string; tone: string } {
  return ORIGIN_META[origin as ProductOrigin] ?? { label: origin || "—", icon: "ri-price-tag-3-line", tone: "bg-gray-400/15 text-gray-500" };
}

/** Utilidad de un renglón con precio de venta y de compra; null si falta alguno. */
export function profitOf(row: Pick<CatalogProduct, "price" | "cost">): { amount: number; margin: number | null } | null {
  // Sin precio de venta (0 = «por cotizar») no hay utilidad que calcular.
  if (row.price == null || row.cost == null || row.price <= 0) return null;
  const amount = row.price - row.cost;
  return { amount, margin: (amount / row.price) * 100 };
}

const CYCLE_LABEL: Record<string, string> = { monthly: "mes", quarterly: "trimestre", yearly: "año" };

/** «$499.00 / mes» para una licencia; solo el precio para lo demás. */
export function priceLabel(
  row: Pick<CatalogProduct, "price" | "currency" | "billingCycle" | "billingModel"> & Partial<Pick<CatalogProduct, "origin" | "type">>,
): string {
  if (row.price == null) return row.billingModel === "tiered" ? "Por escalas" : "—";
  // Un equipo del Punto de venta en $0 todavía no tiene precio de venta.
  if (row.price === 0 && row.origin === "pos" && row.type !== "service") return "Por cotizar";
  const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: row.currency || "MXN" }).format(row.price);
  const cycle = row.billingCycle ? CYCLE_LABEL[row.billingCycle] : null;
  const unit = row.billingModel === "per_unit" ? " por unidad" : "";
  return cycle ? `${money}${unit} / ${cycle}` : money;
}

/** Las plataformas cuya información no se pudo leer completa, para avisarlo. */
export function unreachablePlatforms(platforms: CatalogPlatform[]): string[] {
  return platforms.filter((p) => !p.products.ok || !p.licenses.ok).map((p) => p.product);
}

export interface CatalogFilters {
  query: string;
  origin: ProductOrigin | "all";
  platform: string; // "all" o el nombre del producto
  audience?: ProductAudience | "all";
}

export function filterCatalog(rows: CatalogProduct[], f: CatalogFilters): CatalogProduct[] {
  const q = f.query.trim().toLowerCase();
  return rows.filter((r) => {
    if (f.origin !== "all" && r.origin !== f.origin) return false;
    if (f.platform !== "all" && r.platform !== f.platform) return false;
    if (f.audience && f.audience !== "all" && r.audience !== f.audience) return false;
    if (!q) return true;
    return [r.name, r.sku, r.description].some((v) => String(v ?? "").toLowerCase().includes(q));
  });
}
